/**
 * Add / correct the cancellation or refund reason on a job that is ALREADY
 * in a closed state. Every cancellation made before 2026-10-04 carries no
 * reason at all, so Ops needs a way to fill them in after the fact.
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { serviceOpsApi } from '@/services/api';
import { useStatusCatalogue, reasonsFor, statusLabel } from './useStatusCatalogue';

export const AddReasonDialog = ({ open, lead, onClose, onSaved }) => {
  const catalogue = useStatusCatalogue();
  const [code, setCode] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const options = reasonsFor(catalogue, lead?.status);

  useEffect(() => {
    if (open) {
      setCode(lead?.closure_reason_code || '');
      setNote(lead?.closure_reason_note || '');
    }
  }, [open, lead?.id, lead?.closure_reason_code, lead?.closure_reason_note]);

  const save = async () => {
    if (!code) { toast.error('Pick a reason'); return; }
    if (code === 'OTHER' && !note.trim()) {
      toast.error('A note is required when the reason is "Other"');
      return;
    }
    setSaving(true);
    try {
      await serviceOpsApi.setClosureReason(lead.id, {
        reason_code: code,
        reason_note: note.trim() || null,
      });
      toast.success('Reason saved');
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save the reason');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="add-reason-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">
            Reason — {statusLabel(catalogue, lead?.status)}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="text-slate-500">
            {lead?.customer_name || '—'} · {lead?.mobile || '—'}
            {lead?.city ? ` · ${lead.city}` : ''}
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-slate-500 font-medium">
              Reason
            </label>
            <select
              className="mt-1 w-full border rounded-md px-2 py-2 text-sm bg-white"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              data-testid="add-reason-select"
              style={{ WebkitAppearance: 'menulist', appearance: 'menulist' }}
            >
              <option value="">-- pick a reason --</option>
              {options.map((r) => (
                <option key={r.code} value={r.code}>{r.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-slate-500 font-medium">
              Note {code === 'OTHER' ? '(required)' : '(optional)'}
            </label>
            <Textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="What exactly happened?"
              data-testid="add-reason-note"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="add-reason-cancel">
            Cancel
          </Button>
          <Button onClick={save} disabled={saving} data-testid="add-reason-save">
            {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            Save reason
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddReasonDialog;
